/**
 * Shared Cell Renderer Utilities
 * Reusable rendering functions for DataGrid cells across all domains
 */

import { GridRenderCellParams, GridValidRowModel } from '@mui/x-data-grid';
import React from 'react';
import ExpandableCell, {
  ExpandableListCell,
} from '../../components/ExpandableCell';
import { BaseFieldDefinition, RelationConfig } from '../types/fieldRegistry';

/**
 * Render a boolean value with styled checkmark or X
 */
export function renderBooleanCell(value: unknown): React.ReactElement | string {
  if (value === null || value === undefined) return '—';
  return value ? (
    <span style={{ color: '#22c55e', fontWeight: 'bold' }}>✓</span>
  ) : (
    <span style={{ color: '#ef4444', fontWeight: 'bold' }}>✗</span>
  );
}

/**
 * Render a selection value with label lookup
 */
export function renderSelectionCell(
  value: unknown,
  options: Array<{ value: string; label: string }>
): string {
  if (value === null || value === undefined) return '';
  const option = options.find(opt => opt.value === value);
  return option ? option.label : String(value);
}

/**
 * Render a date value with locale string formatting
 */
export function renderDateCell(value: unknown): string {
  if (!value) return '';
  return new Date(value as string | Date).toLocaleString();
}

/**
 * Render an array of strings with truncation
 */
export function renderStringArrayCell(
  items: string[],
  maxItems: number = 2
): React.ReactElement {
  return <ExpandableListCell items={items || []} maxItems={maxItems} />;
}

/**
 * Render a relational array (objects) with display field
 */
export function renderRelationArrayCell(
  items: Array<Record<string, unknown>>,
  relationConfig: RelationConfig,
  maxItems: number = 2
): React.ReactElement {
  const { displayField, preferredDisplayField, statusField } = relationConfig;

  // Special handling for relations with status field
  if (statusField) {
    const itemStrings: string[] = items.map(
      item => `${item[displayField]} | ${item[statusField] || 'N/A'}`
    );
    return <ExpandableListCell items={itemStrings} maxItems={maxItems} />;
  }

  // Handle preferred display field with fallback
  if (preferredDisplayField) {
    const itemStrings: string[] = items.map(
      item =>
        (item[preferredDisplayField] as string) ||
        (item[displayField] as string)
    );
    return <ExpandableListCell items={itemStrings} maxItems={maxItems} />;
  }

  // Standard relational display (just the name)
  const itemStrings: string[] = items.map(item => String(item[displayField]));
  return <ExpandableListCell items={itemStrings} maxItems={maxItems} />;
}

/**
 * Render HTML content with optional truncation
 */
export function renderHtmlCell(
  content: string,
  maxLength: number = 100
): React.ReactElement {
  return (
    <ExpandableCell content={content} isHtml={true} maxLength={maxLength} />
  );
}

/**
 * Render text content with optional truncation
 */
export function renderTextCell(
  content: string,
  maxLength?: number
): React.ReactElement | string {
  if (!maxLength) return content;
  return (
    <ExpandableCell content={content} isHtml={false} maxLength={maxLength} />
  );
}

/**
 * Create a cell renderer function based on field definition
 * Returns undefined for fields that don't need custom rendering
 */
export function createCellRenderer<TEntity extends GridValidRowModel>(
  field: BaseFieldDefinition<TEntity>
):
  | ((params: GridRenderCellParams<TEntity>) => React.ReactElement | string)
  | undefined {
  // Handle boolean fields
  if (field.dataType === 'boolean') {
    return (params: GridRenderCellParams<TEntity>) => {
      const value = params.row[field.key as keyof TEntity];
      return renderBooleanCell(value);
    };
  }

  // Handle selection fields
  if (field.dataType === 'selection' && field.selectionOptions) {
    return (params: GridRenderCellParams<TEntity>) => {
      const value = params.row[field.key as keyof TEntity];
      return renderSelectionCell(value, field.selectionOptions || []);
    };
  }

  // Handle date fields
  if (field.dataType === 'date') {
    return (params: GridRenderCellParams<TEntity>) => {
      const value = params.row[field.key as keyof TEntity];
      return renderDateCell(value);
    };
  }

  // Handle array of strings (non-relational)
  if (
    field.dataType === 'array' &&
    field.primitiveType === 'string' &&
    !field.isRelation
  ) {
    return (params: GridRenderCellParams<TEntity>) => {
      const items = (params.row[field.key as keyof TEntity] as string[]) || [];
      return renderStringArrayCell(items, field.renderConfig?.maxDisplayItems);
    };
  }

  // Handle relational arrays
  if (field.isRelation && field.relationConfig) {
    return (params: GridRenderCellParams<TEntity>) => {
      const items =
        (params.row[field.key as keyof TEntity] as unknown as Array<
          Record<string, unknown>
        >) || [];
      return renderRelationArrayCell(
        items,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        field.relationConfig!,
        field.renderConfig?.maxDisplayItems
      );
    };
  }

  // Handle HTML content or truncation
  if (
    field.renderConfig?.formatAs === 'html' ||
    field.renderConfig?.maxDisplayLength
  ) {
    return (params: GridRenderCellParams<TEntity>) => {
      const value = params.row[field.key as keyof TEntity];
      if (!value) return '';

      if (field.renderConfig?.formatAs === 'html') {
        return renderHtmlCell(
          value as string,
          field.renderConfig?.maxDisplayLength
        );
      } else {
        return renderTextCell(
          value as string,
          field.renderConfig?.maxDisplayLength
        );
      }
    };
  }

  // No custom rendering needed
  return undefined;
}

/**
 * Create a valueGetter for computed fields
 */
export function createComputedValueGetter<TEntity extends GridValidRowModel>(
  field: BaseFieldDefinition<TEntity>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): ((_value: any, row: TEntity) => any) | undefined {
  if (!field.isComputed || !field.computedFrom) {
    return undefined;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (_value: any, row: TEntity) => {
    const sourceArray = row[field.computedFrom as keyof TEntity];
    return Array.isArray(sourceArray) ? sourceArray.length : 0;
  };
}
